"use client";

import { MainButton, SectionCard } from "@repo/dashboard-core";
import { countries } from "@repo/dashboard-core/data";
import { Alert, Checkbox, Select, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useTranslations } from "next-intl";
import { useState } from "react";

import type {
  CheckInput,
  PackageInfo,
  PackageToken,
} from "@/lib/types/phone-checker";

/** Twilio's own maximum length per Identity Match field — over it is a 60618. */
const IDENTITY_MAX_LENGTH: Record<string, number> = {
  firstName: 128,
  lastName: 128,
  addressLine1: 256,
  city: 128,
  state: 128,
  postalCode: 10,
  dateOfBirth: 8,
};

const IDENTITY_FIELDS = [
  "firstName",
  "lastName",
  "addressLine1",
  "city",
  "state",
  "postalCode",
] as const;

type FormValues = {
  phoneNumber: string;
  country: string;
  packages: string[];
  firstName: string;
  lastName: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  dateOfBirth: string;
  lastVerifiedDate: string;
};

export function CheckForm({
  catalogue,
  defaultCountry,
  disabled,
  pending,
  onSubmit,
}: {
  catalogue: PackageInfo[];
  defaultCountry?: string;
  disabled: boolean;
  pending: boolean;
  onSubmit: (input: CheckInput) => void;
}) {
  const t = useTranslations("phone-checker");
  const tv = useTranslations("validation");
  const [showIdentity, setShowIdentity] = useState(false);

  const form = useForm<FormValues>({
    initialValues: {
      phoneNumber: "",
      country: defaultCountry ?? "",
      packages: catalogue.filter((p) => p.default).map((p) => p.package),
      firstName: "",
      lastName: "",
      addressLine1: "",
      city: "",
      state: "",
      postalCode: "",
      dateOfBirth: "",
      lastVerifiedDate: "",
    },
    validate: {
      phoneNumber: (value) => (value.trim() ? null : tv("required")),
      packages: (value) =>
        value.length > 0 ? null : t("form.checks-required"),
      // Twilio takes both dates as YYYYMMDD with no separators, and answers a
      // malformed one with a refused package the caller already paid for.
      dateOfBirth: (value) =>
        !value || /^\d{8}$/.test(value) ? null : t("form.date-format"),
      lastVerifiedDate: (value) =>
        !value || /^\d{8}$/.test(value) ? null : t("form.date-format"),
    },
  });

  const selected = form.values.packages as PackageToken[];
  const needsIdentity = selected.includes("identity_match");
  const needsConsentDate = selected.includes("reassigned_number");

  const handleSubmit = (values: FormValues) => {
    const identity = Object.fromEntries(
      [...IDENTITY_FIELDS, "dateOfBirth"]
        .map((field) => [field, values[field as keyof FormValues]] as const)
        .filter(([, value]) => typeof value === "string" && value.trim()),
    ) as CheckInput["identity"];

    onSubmit({
      phoneNumber: values.phoneNumber,
      ...(values.country && { country: values.country }),
      packages: selected,
      // Only sent when the package that needs it was actually asked for —
      // putting a name and address on the wire for a request that did not ask
      // for Identity Match would be paying nothing to leak something.
      ...(needsIdentity &&
        identity &&
        Object.keys(identity).length > 0 && { identity }),
      ...(needsConsentDate &&
        values.lastVerifiedDate && {
          lastVerifiedDate: values.lastVerifiedDate,
        }),
    });
  };

  return (
    <SectionCard title={t("form.title")} description={t("form.description")}>
      <form className="space-y-5" onSubmit={form.onSubmit(handleSubmit)}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <TextInput
            className="sm:col-span-2"
            label={t("form.number-label")}
            placeholder={t("form.number-placeholder")}
            description={t("form.number-help")}
            radius="md"
            type="tel"
            autoComplete="tel"
            {...form.getInputProps("phoneNumber")}
          />
          <Select
            label={t("form.country-label")}
            placeholder={t("form.country-placeholder")}
            description={t("form.country-help")}
            data={countries}
            searchable
            clearable
            radius="md"
            {...form.getInputProps("country")}
          />
        </div>

        <Checkbox.Group
          label={t("form.checks-label")}
          description={t("form.checks-help")}
          {...form.getInputProps("packages")}
        >
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {catalogue.map((info) => (
              <Checkbox
                key={info.package}
                value={info.package}
                label={t(`package.${info.package}`)}
                description={[
                  t(`package-description.${info.package}`),
                  info.requiresInput ? t("form.needs-details") : null,
                  info.requiresApproval ? t("form.needs-approval") : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              />
            ))}
          </div>
        </Checkbox.Group>

        {(needsIdentity || needsConsentDate) && (
          <>
            <button
              type="button"
              className="block text-sm font-medium underline underline-offset-4"
              onClick={() => setShowIdentity((open) => !open)}
              aria-expanded={showIdentity}
            >
              {showIdentity ? t("form.hide-details") : t("form.show-details")}
            </button>

            {/* Plain conditional, not Mantine's Collapse: the animated
                version measures its own content height, and it was rendering
                the panel clipped behind the submit button. A disclosure on a
                form does not need to animate. */}
            {showIdentity && (
              <div className="space-y-4">
                {needsIdentity && (
                  <Alert color="blue" radius="md">
                    {t("form.identity-help")}
                  </Alert>
                )}

                {needsIdentity && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {IDENTITY_FIELDS.map((field) => (
                      <TextInput
                        key={field}
                        label={t(`form.${field}`)}
                        radius="md"
                        maxLength={IDENTITY_MAX_LENGTH[field]}
                        {...form.getInputProps(field)}
                      />
                    ))}
                    <TextInput
                      label={t("form.dateOfBirth")}
                      description={t("form.date-help")}
                      placeholder="19900425"
                      radius="md"
                      maxLength={8}
                      inputMode="numeric"
                      {...form.getInputProps("dateOfBirth")}
                    />
                  </div>
                )}

                {needsConsentDate && (
                  <TextInput
                    label={t("form.consent-date-label")}
                    description={t("form.consent-date-help")}
                    placeholder="20211015"
                    radius="md"
                    maxLength={8}
                    inputMode="numeric"
                    {...form.getInputProps("lastVerifiedDate")}
                  />
                )}
              </div>
            )}
          </>
        )}

        <MainButton type="submit" disabled={disabled || pending}>
          {pending ? t("form.submitting") : t("form.submit")}
        </MainButton>
      </form>
    </SectionCard>
  );
}
